import React, { useMemo } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';
import ReactPlaceholder from 'react-placeholder/lib';

import type {
  IScheduleBlockAvailable,
  ISortedAvailableShift,
  ISortedGroupedSchedule,
  ISortedShift,
} from '@/types/schedule';

import type { IRenderWeekdayProps } from '@/types/calendar';

import { useAppSelector, useCalendarShifts, useCheckCollisionWithSchedule, useTimezone, useWeek } from '@/hooks';
import {
  alignAllShifts,
  excludeIllegalShifts,
  getDisplayBlocksForMultiDayView,
  groupAllShifts,
  isBlockCoveredByDst,
  transformDlsShift,
} from '@/helpers';
import { getAvailableShifts } from '@/store/slices/scheduleSlice';
import { AllShiftItemType, CalendarDisplayRange } from '@/store/enums';
import {
  selectAvailableShifts,
  selectGrouppedSchedulesList,
  selectIsPendingAction,
  selectSelectedSchedulesIds,
} from '@/store/selectors/scheduleSelectors';
import { DlsBlock } from '@/components/common';
import { ShiftRow } from '@/components/agent/my-schedule/ShiftRow';

const allShiftsSelector = createSelector(
  selectAvailableShifts,
  selectGrouppedSchedulesList,
  selectIsPendingAction(getAvailableShifts.typePrefix),
  selectSelectedSchedulesIds,
  (availableShifts, grouppedSchedulesList, isFetching, selectedSchedulesIds) => ({
    availableShifts,
    grouppedSchedulesList,
    isFetching,
    selectedSchedulesIds,
  }),
);

const AllShifts = ({ day, week }: IRenderWeekdayProps): React.ReactElement => {
  const { selectedDays } = useWeek();
  const { timezoneName } = useTimezone();
  const { splitShift, mapToSorted } = useCalendarShifts<IScheduleBlockAvailable>({
    day,
    week,
  });

  const { availableShifts, grouppedSchedulesList, isFetching, selectedSchedulesIds } =
    useAppSelector(allShiftsSelector);

  const { checkCollisionWithSchedule } = useCheckCollisionWithSchedule();

  const mySchedules = useMemo<ISortedShift<ISortedGroupedSchedule>[]>(
    () =>
      grouppedSchedulesList.map<ISortedShift<ISortedGroupedSchedule>>(item => ({
        ...item,
        displayBlocks: getDisplayBlocksForMultiDayView(week, week.timezone, item.itemData, selectedDays, day),
      })),
    [day, grouppedSchedulesList, selectedDays, week],
  );

  /* Prepare groupped available shifts */
  const myAvailableShifts = useMemo<ISortedAvailableShift[]>(() => {
    const mappedAvailableShifts: ISortedAvailableShift[] = availableShifts
      .flatMap(shift => splitShift(shift, timezoneName))
      .map(item => mapToSorted(item, AllShiftItemType.shiftAvailable))
      .map(item => ({
        ...item,
        displayBlocks: getDisplayBlocksForMultiDayView(week, week.timezone, item.itemData, selectedDays, day),
      }))
      .filter(
        shift =>
          selectedSchedulesIds.includes(shift.scheduleId) &&
          shift.dayNumber === day.dayNumber &&
          shift.monthNumber === day.monthNumber &&
          !checkCollisionWithSchedule(shift, mySchedules),
      );

    return mappedAvailableShifts;
  }, [
    availableShifts,
    splitShift,
    timezoneName,
    mapToSorted,
    week,
    selectedDays,
    day,
    selectedSchedulesIds,
    checkCollisionWithSchedule,
    mySchedules,
  ]);

  const allShifts = useMemo<ISortedShift[][][]>(() => {
    const newAllShifts: ISortedShift[] = [...myAvailableShifts, ...grouppedSchedulesList]
      .sort((a, b) => a.itemData.startBlock - b.itemData.startBlock)
      .filter(
        item =>
          selectedSchedulesIds.includes(item.scheduleId) &&
          item.monthNumber === day.monthNumber &&
          item.dayNumber === day.dayNumber,
      )
      .map<ISortedShift>(shift => {
        const shiftWithDisplayBlocks = {
          ...shift,
          displayBlocks: getDisplayBlocksForMultiDayView(week, timezoneName, shift.itemData, selectedDays, day),
        };

        return selectedDays === CalendarDisplayRange.ONE_DAY
          ? shiftWithDisplayBlocks
          : transformDlsShift(shiftWithDisplayBlocks, week);
      })
      .filter(
        shift => selectedDays === CalendarDisplayRange.ONE_DAY || !isBlockCoveredByDst(week, shift.displayBlocks),
      );

    const grouppedShifts = groupAllShifts(newAllShifts);

    const legalShifts = excludeIllegalShifts(grouppedShifts);

    return alignAllShifts(legalShifts);
  }, [myAvailableShifts, grouppedSchedulesList, selectedSchedulesIds, day, week, timezoneName, selectedDays]);

  const isPending = isFetching && allShifts.length === 0;

  return (
    <ReactPlaceholder ready={!isPending} showLoadingAnimation type="rect" className="react-placeholder">
      <Container>
        {allShifts.map(row => (
          <ShiftRow key={row[0][0].itemData.id + row[0][0].displayBlocks.startBlock} row={row} day={day} week={week} />
        ))}
        <DlsBlock day={day} week={week} selectedDays={selectedDays} />
      </Container>
    </ReactPlaceholder>
  );
};

export { AllShifts };

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;
